import { FC, ElementType, ForwardedRef, forwardRef, HTMLProps } from 'react';
import { css, Theme } from '@emotion/react';
import { Font } from 'client/Theme';

export const textStyle = (theme: Theme) => css`
  margin: 0;
  font-family: ${Font.body};
  color: ${theme.color.foreground};

  ${['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map((x) => `&.${x}`).join(', ')} {
    font-family: ${Font.header};
    font-weight: 500;
  }

  &.h1 {
    font-size: 6rem;
    line-height: 1.167;
  }

  &.h2 {
    font-size: 3.75rem;
    line-height: 1.2;
  }

  &.h3 {
    font-size: 3rem;
    line-height: 1.167;
  }

  &.h4 {
    font-size: 1.75rem;
    line-height: 1.235;
  }

  &.h5 {
    font-size: 1.5rem;
    line-height: 1.334;
  }

  &.h6 {
    font-size: 1.25rem;
    line-height: 1.6;
  }

  p& {
    font-weight: 400;
  }

  a {
    color: ${theme.color.primary[100]};
  }

  label& {
    font-weight: 400;
    color: ${theme.color.grey[100]};
  }

  &.body1 {
    font-size: 1rem;
    line-height: 1.5;
  }

  &.body2 {
    font-size: 0.875rem;
    line-height: 1.43;
    font-weight: 400;
    color: ${theme.color.grey[100]};
  }

  &.caption {
    font-size: 0.75rem;
    line-height: 1.66;
  }

  &.fineprint {
    font-size: 0.625rem;
    color: ${theme.color.grey[100]};
  }

  &.statistic {
    font-size: 0.875rem;
    font-weight: 400;
    color: ${theme.color.grey[100]};

    small {
      font-size: 0.875rem;
    }

    b {
      font-size: 1.25rem;
      color: ${theme.color.foreground};
    }
  }

  &.ellipsis {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

export type TypographyVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2' | 'caption' | 'fineprint' | 'statistic';

const typographyVariantToElement: Record<TypographyVariant, keyof JSX.IntrinsicElements> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  body1: 'p',
  body2: 'p',
  caption: 'p',
  fineprint: 'p',
  statistic: 'p',
} as const;

export interface TypographyProps extends HTMLProps<HTMLElement> {
  variant?: TypographyVariant;
  component?: ElementType;
  noWrap?: boolean;
  for?: string;
  href?: string;
  target?: '_blank';
  ref?: ForwardedRef<any>;
}

export const Typography: FC<TypographyProps> = forwardRef(
  ({ variant = 'body1', component, noWrap = false, className, children, ...other }, ref) => {
    const Element = (component || typographyVariantToElement[variant] || 'div') as 'div';

    return (
      <Element
        ref={ref as any}
        css={textStyle}
        className={`typography ${variant} ${noWrap ? 'ellipsis' : ''} ${className || ''}`}
        {...other}
      >
        {children}
      </Element>
    );
  }
);
